import { z } from 'zod'

// ─── DTOs voor verlof saldo-beheer + stats ───────────────────────────────────

/**
 * Eén verlofemmer (wettelijk / bovenwettelijk / atv / tvtWerk / tvtReis).
 * Alle uren-waarden zijn decimale uren (bijv. 8.0 = 8 uur).
 */
export type LeaveBalanceEmmer = {
  toegekend: number
  actueel: number
  opgenomen: number
  gereserveerd: number
  beschikbaar: number
  vervalDatum: string | null  // ISO date
}

/**
 * Bijzonder verlof (CAO art. 38) trekt GEEN saldo (FO-089). De kolom toont
 * daarom enkel een teller van de opgenomen bijzonder-uren in het peiljaar, niet
 * een beschikbaar-saldo.
 */
export type LeaveBalanceBijzonder = {
  /** Opgenomen (DEFINITIEF geboekte) bijzonder-uren in het peiljaar. */
  opgenomen: number
}

/**
 * Saldo-rij per medewerker, gebruikt in GET /api/v1/leave/v2/balances.
 *
 * `emmers` houdt de KLASSIEKE potjes apart (wettelijk/bovenwettelijk/atv/
 * tvtWerk/tvtReis) voor het sub-detail; de UI vat ze samen tot de 5
 * opdrachtgever-emmers (vakantie = wettelijk + bovenwettelijk, tvt = werk + reis)
 * via `vakantieEmmer` / `tvtEmmer`. `bijzonder` (opgenomen-teller) en `senioren`
 * (FO-089) komen erbij.
 */
export type LeaveBalanceRow = {
  employeeId: string
  naam: string
  personeelsnummer: string | null
  contractUren: number
  atvEnabled: boolean
  emmers: {
    wettelijk: LeaveBalanceEmmer | null
    bovenwettelijk: LeaveBalanceEmmer | null
    atv: LeaveBalanceEmmer | null
    tvtWerk: LeaveBalanceEmmer | null
    tvtReis: LeaveBalanceEmmer | null
    bijzonder: LeaveBalanceBijzonder
    senioren: LeaveBalanceEmmer | null
  }
  // Spec 38 — server-side signaal: herverdeling (vervalt-eerst) zou een emmer die
  // dit jaar vervalt (bv. ATV per 31-12) voller maken. Markeert de Herverdeel-knop.
  herverdelingMogelijk: boolean
}

// ─── FO-089 — de 5 opdrachtgever-emmers (vakantie/atv/tvt/bijzonder/senioren) ──
//
// De saldo-tab toont per medewerker vijf emmers naast elkaar. Vakantie en TVT
// zijn samenvattingen van twee klassieke potjes; bijzonder is een opgenomen-
// teller (geen saldo); senioren komt uit de SENIORENVERLOF-entitlements. Gedeeld
// request-contract (server + client) zodat de filter/highlight-keuze één bron heeft.

export const LEAVE_BALANCE_EMMERS = [
  'VAKANTIE',
  'ATV',
  'TVT',
  'BIJZONDER',
  'SENIOREN',
] as const
export type LeaveBalanceEmmerKey = (typeof LEAVE_BALANCE_EMMERS)[number]

export const LEAVE_BALANCE_EMMER_LABELS: Record<LeaveBalanceEmmerKey, string> = {
  VAKANTIE: 'Vakantie',
  ATV: 'ATV',
  TVT: 'TVT',
  BIJZONDER: 'Bijzonder',
  SENIOREN: 'Senioren',
}

/** Emmer-keuze in de saldo-tab: één van de 5, of 'ALLE' (geen highlight/filter-doel). */
export type LeaveBalanceEmmerFilter = 'ALLE' | LeaveBalanceEmmerKey

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** Vroegste van twee ISO-datums (YYYY-MM-DD-vergelijking == chronologisch). */
function vroegsteDatum(a: string | null, b: string | null): string | null {
  if (!a) return b
  if (!b) return a
  return a <= b ? a : b
}

/**
 * Combineer twee klassieke emmers tot één opdrachtgever-emmer (bv. wettelijk +
 * bovenwettelijk → Vakantie, of tvtWerk + tvtReis → TVT). De vervaldatum is de
 * EERSTVERVALLENDE van de twee (zodat de verval-waarschuwing op het strengste
 * potje slaat). `null` als beide ontbreken. Eén bron voor server (filter) en UI
 * (kolom), zodat de samenvatting niet uiteenloopt.
 */
export function combineLeaveEmmers(
  a: LeaveBalanceEmmer | null,
  b: LeaveBalanceEmmer | null,
): LeaveBalanceEmmer | null {
  if (!a && !b) return null
  const x = a ?? { toegekend: 0, actueel: 0, opgenomen: 0, gereserveerd: 0, beschikbaar: 0, vervalDatum: null }
  const y = b ?? { toegekend: 0, actueel: 0, opgenomen: 0, gereserveerd: 0, beschikbaar: 0, vervalDatum: null }
  return {
    toegekend: round2(x.toegekend + y.toegekend),
    actueel: round2(x.actueel + y.actueel),
    opgenomen: round2(x.opgenomen + y.opgenomen),
    gereserveerd: round2(x.gereserveerd + y.gereserveerd),
    beschikbaar: round2(x.beschikbaar + y.beschikbaar),
    vervalDatum: vroegsteDatum(a?.vervalDatum ?? null, b?.vervalDatum ?? null),
  }
}

/** Samengevatte Vakantie-emmer (wettelijk + bovenwettelijk) van een saldo-rij. */
export function vakantieEmmer(row: LeaveBalanceRow): LeaveBalanceEmmer | null {
  return combineLeaveEmmers(row.emmers.wettelijk, row.emmers.bovenwettelijk)
}

/** Samengevatte TVT-emmer (werk + reis) van een saldo-rij. */
export function tvtEmmer(row: LeaveBalanceRow): LeaveBalanceEmmer | null {
  return combineLeaveEmmers(row.emmers.tvtWerk, row.emmers.tvtReis)
}

/**
 * Normaliseer een (mogelijk legacy) emmer-keuze naar de 5 opdrachtgever-emmers of
 * 'ALLE'. Eén bron, gedeeld door de API-handler (filter-doel) én de saldo-tab-UI
 * (Select-waarde / highlight / sort), zodat een oude deeplink (`WETTELIJK`,
 * `TVT_WERK`, ...) overal hetzelfde, geldig gedrag geeft i.p.v. een 400 of een
 * blanco select.
 */
export function normalizeBalanceEmmer(v: string): LeaveBalanceEmmerFilter {
  switch (v) {
    case 'WETTELIJK':
    case 'BOVENWETTELIJK':
    case 'VAKANTIE':
      return 'VAKANTIE'
    case 'TVT_WERK':
    case 'TVT_REIS':
    case 'TVT':
      return 'TVT'
    case 'ATV':
      return 'ATV'
    case 'BIJZONDER':
      return 'BIJZONDER'
    case 'SENIOREN':
      return 'SENIOREN'
    default:
      return 'ALLE'
  }
}

/**
 * Response-type voor GET /api/v1/verlof/saldo (lijst).
 */
export type LeaveBalancesListResponse = {
  data: LeaveBalanceRow[]
  meta: { total: number; page: number; pageSize: number; peildatum: string }
}

/**
 * Statistieken voor de KPI-kaarten op de verlof-beheer dashboard.
 * Scope-afhankelijke velden (bijnaVerval, negatiefSaldo, tvtBoven80) zijn 0
 * wanneer de ingelogde gebruiker geen admin/hr-scope heeft.
 */
export type LeaveStatsResponse = {
  openstaand: number
  bijTeamleider: number
  klaarVoorHr: number
  vandaagDefinitief: number
  bijnaVerval: number       // alleen bij scope=admin|hr; anders 0
  negatiefSaldo: number     // idem
  tvtBoven80: number        // idem
  // batch-69 tvt-saldi (FO-522 §C9) - medewerkers met minstens één emmer in de
  // min volgens de mutatie-ledger (zonder vastgeklikte verlofschuld), voor de
  // teller "Saldi in de min" op het verlofdashboard. Idem scope-regel.
  saldiInDeMin: number
}

// ─── Spec 39 — verlofschuld (negatief saldo expliciet i.p.v. stil weggestreept) ─

/** Emmer-types waarop verlofschuld kan ontstaan (de autoAfboek-emmers). */
export const LEAVE_DEBT_EMMER_TYPES = ['WETTELIJK', 'BOVENWETTELIJK', 'ATV'] as const
export type LeaveDebtEmmerType = (typeof LEAVE_DEBT_EMMER_TYPES)[number]

/**
 * Status van een verlofschuld. De drie niet-OPEN waarden zijn tevens de
 * afhandel-methoden (de status na afhandeling = de gekozen methode).
 */
export const LEAVE_DEBT_STATUS = [
  'OPEN',
  'VERREKEND_VERLOFRECHT',
  'VERREKEND_LOON',
  'KWIJTGESCHOLDEN',
] as const
export type LeaveDebtStatus = (typeof LEAVE_DEBT_STATUS)[number]

/** Afhandel-methoden (de niet-OPEN statussen). Default = verrekenen met verlofrecht (PO-besluit). */
export const LEAVE_DEBT_METHODES = [
  'VERREKEND_VERLOFRECHT',
  'VERREKEND_LOON',
  'KWIJTGESCHOLDEN',
] as const
export type LeaveDebtMethode = (typeof LEAVE_DEBT_METHODES)[number]

export const LEAVE_DEBT_STATUS_LABELS: Record<LeaveDebtStatus, string> = {
  OPEN: 'Open',
  VERREKEND_VERLOFRECHT: 'Verrekend met nieuw verlofrecht',
  VERREKEND_LOON: 'Verrekend met loon',
  KWIJTGESCHOLDEN: 'Kwijtgescholden',
}

export const LEAVE_DEBT_EMMER_LABELS: Record<LeaveDebtEmmerType, string> = {
  WETTELIJK: 'Wettelijk',
  BOVENWETTELIJK: 'Bovenwettelijk',
  ATV: 'ATV',
}

/** Eén verlofschuld-rij (read-DTO voor overzicht + per-medewerker). */
export type LeaveDebtDTO = {
  id: string
  employeeId: string
  naam: string
  personeelsnummer: string | null
  type: LeaveDebtEmmerType
  originYear: number
  uren: number
  status: LeaveDebtStatus
  voorstelMethode: LeaveDebtMethode
  voorstelDoelJaar: number | null
  reden: string | null
  resolvedBy: string | null
  resolvedAt: string | null // ISO
  resolutionDoelJaar: number | null
  createdAt: string // ISO
}

/**
 * Een huidige over-opgenomen emmer die nog NIET als verlofschuld is vastgeklikt
 * (per-medewerker-detail: laat HR ad-hoc crystalliseren vóór de jaarovergang).
 */
export type LeaveDebtNietVastgeklikt = {
  entitlementId: string
  type: LeaveDebtEmmerType
  year: number
  schuldUren: number // huidige over-opname (positief)
}

/**
 * Een huidige over-opgenomen emmer die nog NIET als verlofschuld is vastgeklikt,
 * cross-medewerker (HR-overzicht: ook deze negatieve saldi moeten zichtbaar zijn
 * en mogen nooit stil verdwijnen). `schuldUren` is indicatief op basis van de
 * entitlement-cache (`usedHours - hours`); het vastklikken doet de precieze,
 * mutatie-gebaseerde berekening.
 */
export type LeaveDebtNietVastgekliktRow = {
  employeeId: string
  naam: string
  personeelsnummer: string | null
  entitlementId: string
  type: LeaveDebtEmmerType
  year: number
  schuldUren: number
}

export type LeaveDebtListResponse = {
  data: LeaveDebtDTO[]
  nietVastgeklikt: LeaveDebtNietVastgekliktRow[]
  meta: { total: number; openTotaalUren: number; nietVastgekliktTotaalUren: number }
}

export type LeaveDebtEmployeeResponse = {
  data: {
    employeeId: string
    debts: LeaveDebtDTO[]
    nietVastgeklikt: LeaveDebtNietVastgeklikt[]
  }
}

/**
 * Gedeeld afhandel-request-schema (HR bevestigt het voorstel of overschrijft).
 * Door de API (`.parse`) én het form (pre-validatie) gebruikt — één bron.
 * `doelJaar` alleen relevant bij VERREKEND_VERLOFRECHT (jaar van het verlofrecht
 * waartegen verrekend wordt; default = originYear + 1).
 */
export const LeaveDebtAfhandelSchema = z.object({
  methode: z.enum(LEAVE_DEBT_METHODES),
  toelichting: z.string().trim().min(10, 'Geef een reden van minstens 10 tekens'),
  doelJaar: z.number().int().min(2000).max(2100).optional(),
  // Verplichte bevestiging bij VERREKEND_LOON: er is een schriftelijke grondslag
  // (arbeidsovereenkomst / personeelshandboek / aparte afspraak). Zonder grondslag
  // weigert de API de loon-route (juridische controle nodig). Genegeerd bij de
  // andere methoden.
  schriftelijkeAfspraak: z.boolean().optional(),
})
export type LeaveDebtAfhandelRequest = z.infer<typeof LeaveDebtAfhandelSchema>

/** Crystalliseer-request (vastklikken); `dryRun` voor een read-only preview. */
export const LeaveDebtCrystalliseerSchema = z.object({
  dryRun: z.boolean().optional(),
})
export type LeaveDebtCrystalliseerRequest = z.infer<typeof LeaveDebtCrystalliseerSchema>

/** Resultaat/preview van het vastklikken (per over-opgenomen emmer). */
export type LeaveDebtCrystalliseerResult = {
  dryRun: boolean
  vastgeklikt: Array<{
    type: LeaveDebtEmmerType
    year: number
    uren: number
    voorstelMethode: LeaveDebtMethode
    voorstelDoelJaar: number | null
  }>
}

// ─── FO-071 (batch-20) — gedeelde verlof-classificatie-mapping ────────────────
//
// Eén bron voor "hoe heet dit verlof?", gedeeld door de verlofmodule
// (`verlof/_types.ts` her-exporteert deze maps) en de verlof-exports
// (`services/exports/engine.ts`). Voorkomt dat losse label-tabellen uiteenlopen
// (Productvisie principe 4: één waarheid per gegeven).
//
// De waarden spiegelen de Prisma-enums `LeaveType` / `VerlofMethode` /
// `BijzonderReden` / `LeaveStatus` (`packages/db/prisma/schema.prisma`). Een
// enum-waarde die hier nog niet staat valt via `classificeerVerlof` op een nette,
// leesbare fallback (`humanizeEnumValue`) - nooit op de kale enum-string.

/** Alle `VerlofMethode`-waarden (Prisma-enum `VerlofMethode`), in UI-volgorde. */
export const VERLOF_METHODEN = [
  'VERLOF',
  'ATV',
  'TVT',
  'BIJZONDER',
  'OUDERSCHAP',
  'SENIOREN',
] as const

/** Methode-keuze door de medewerker (Prisma `VerlofMethode`). */
export type VerlofMethode = (typeof VERLOF_METHODEN)[number]

/** Sub-reden bij methode `BIJZONDER` (CAO art. 38; Prisma `BijzonderReden`). */
export type BijzonderReden =
  | 'HUWELIJK_EIGEN'
  | 'HUWELIJK_KIND'
  | 'HUWELIJK_FAMILIELID'
  | 'OVERLIJDEN_PARTNER_KIND'
  | 'OVERLIJDEN_OUDER'
  | 'OVERLIJDEN_SCHOONOUDER'
  | 'OVERLIJDEN_FAMILIELID'
  | 'VERHUIZING'
  | 'JUBILEUM_25'
  | 'DOKTERSBEZOEK'
  | 'OVERIG'

/** Workflow-status van een verlofverzoek (Prisma `LeaveStatus`, V2-set). */
export type VerlofStatus =
  | 'CONCEPT'
  | 'INGEDIEND'
  | 'AANVULLENDE_VRAAG'
  | 'GOEDGEKEURD_TEAMLEIDER'
  | 'AFGEKEURD_TEAMLEIDER'
  | 'GECLASSIFICEERD'
  | 'DEFINITIEF'
  | 'OVERRULED'
  | 'AFGEKEURD'
  | 'INGETROKKEN'

/**
 * Alle `LeaveType`-waarden -> NL-label. `Record<string, string>` zodat callers
 * die met een rauwe Prisma-enum-string indexeren (exports/engine, dienstenplein)
 * geen cast nodig hebben; een onbekende key vangt `classificeerVerlof` af.
 */
export const LEAVE_TYPE_LABELS: Record<string, string> = {
  ONGECLASSIFICEERD: 'Nog te classificeren',
  VAKANTIE: 'Vakantie',
  ZIEK: 'Ziek',
  BIJZONDER: 'Bijzonder verlof',
  ONBETAALD: 'Onbetaald verlof',
  MEDISCH: 'Medisch',
  OVERIG: 'Overig',
  ATV: 'ATV',
  TVT_WERK: 'TVT (werk)',
  TVT_REIS: 'TVT (reis)',
  ZWANGERSCHAP: 'Zwangerschapsverlof',
  GEBOORTE: 'Geboorteverlof',
  GEBOORTE_AANVULLEND: 'Aanvullend geboorteverlof',
  OUDERSCHAP_BETAALD: 'Ouderschapsverlof (betaald)',
  OUDERSCHAP_ONBETAALD: 'Ouderschapsverlof (onbetaald)',
  OUDERSCHAP_AANVULLEND: 'Aanvullend ouderschapsverlof',
  SENIORENVERLOF: 'Seniorenverlof',
}

/** Alle `VerlofMethode`-waarden -> NL-label (incl. OUDERSCHAP/SENIOREN). */
export const VERLOF_METHODE_LABELS: Record<VerlofMethode, string> = {
  VERLOF: 'Verlof',
  ATV: 'ATV',
  TVT: 'TVT',
  BIJZONDER: 'Bijzonder',
  OUDERSCHAP: 'Ouderschapsverlof',
  SENIOREN: 'Seniorenverlof',
}

// ─── Spec 96 (batch-48) — wie mag welke verlofmethode kiezen? ─────────────────
//
// EEN centrale bron voor "welke methode mag een medewerker in de PORTAL zelf
// kiezen". Álle portal-keuzelijsten (aanvraagmodal, wijzig-flow, campagne-wizard)
// en álle portal-schrijfpaden in de API voeden zich hieruit; lokale opsommingen
// zijn precies de drift die ouderschapsverlof drie keer heeft laten terugsluipen
// (drift-guard: `apps/api/src/lib/__tests__/verlof-methode-drift.test.ts`).
//
// De zes methoden vallen in drie disjuncte groepen (de partitie-invariant
// hieronder dwingt af dat een NIEUWE Prisma-enumwaarde een bewuste keuze kost):
//
//   PORTAL_KIESBARE_VERLOFMETHODEN  - de medewerker kiest deze zelf in de portal.
//   ALLEEN_HR_VERLOFMETHODEN        - nooit vanuit een portal-schrijfpad, ook niet
//                                     op een bestaande aanvraag (HR-traject).
//   HR_CLASSIFICATIE_VERLOFMETHODEN - geen portal-keuze, maar HR mag ze bij
//                                     classificatie/namens-flow wél zetten.

/**
 * De verlofmethoden die een medewerker in de portal ZELF kiest.
 *
 * - `VERLOF` is de keuze-loze standaard (procedure-correctie Deel 1): de
 *   medewerker geeft alleen periode + uren + reden, HR classificeert daarna.
 * - `SENIOREN` is de bewuste batch-6-uitzondering: seniorenverlof heeft een eigen
 *   emmer en kiest de medewerker expliciet.
 *
 * `OUDERSCHAP` staat hier bewust NIET in: dat traject (kind, betaald/onbetaald,
 * UWV/WAZO, loondoorbetaling) plant HR in via de backoffice (spec 45).
 */
export const PORTAL_KIESBARE_VERLOFMETHODEN = ['VERLOF', 'SENIOREN'] as const
export type PortalKiesbareVerlofMethode = (typeof PORTAL_KIESBARE_VERLOFMETHODEN)[number]

/**
 * Methoden die een portal-schrijfpad NOOIT mag zetten of indienen, ook niet op
 * een bestaande (namens aangemaakte) aanvraag. De medewerker houdt zicht op het
 * saldo, de classificatie blijft bij HR.
 */
export const ALLEEN_HR_VERLOFMETHODEN = ['OUDERSCHAP'] as const
export type AlleenHrVerlofMethode = (typeof ALLEEN_HR_VERLOFMETHODEN)[number]

/**
 * Classificatie-uitkomsten: geen keuze in de portal-UI, maar HR mag ze bij
 * classificatie of via de namens-backoffice-flow wél zetten. Een medewerker mag
 * een zo geclassificeerde aanvraag dus gewoon indienen - het is HR's keuze, niet
 * de zijne.
 */
export const HR_CLASSIFICATIE_VERLOFMETHODEN = ['ATV', 'TVT', 'BIJZONDER'] as const
export type HrClassificatieVerlofMethode = (typeof HR_CLASSIFICATIE_VERLOFMETHODEN)[number]

/**
 * Partitie-invariant: de drie groepen dekken samen exact `VERLOF_METHODEN`, zonder
 * overlap. Puur een compile-time-check - wie `VERLOF_METHODEN` uitbreidt (de
 * spiegel van de Prisma-enum) krijgt hier een typefout tot de nieuwe waarde
 * bewust in één van de drie groepen staat.
 *
 * LET OP: dit koppelt aan `VERLOF_METHODEN`, niet rechtstreeks aan de Prisma-enum.
 * Een nieuwe `VerlofMethode` in `schema.prisma` die hier niet wordt bijgezet valt
 * dus buiten deze check - de drift-guard-test bewaakt die kant.
 */
type VerlofMethodeGroepen =
  | PortalKiesbareVerlofMethode
  | AlleenHrVerlofMethode
  | HrClassificatieVerlofMethode
type ZelfdeUnie<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never
export const VERLOF_METHODE_PARTITIE_OK: ZelfdeUnie<VerlofMethodeGroepen, VerlofMethode> = true

/** Mag de medewerker deze methode zelf kiezen in de portal? */
export function isPortalKiesbareVerlofMethode(
  methode: string | null | undefined,
): methode is PortalKiesbareVerlofMethode {
  return methode != null && (PORTAL_KIESBARE_VERLOFMETHODEN as readonly string[]).includes(methode)
}

/** Is deze methode voorbehouden aan HR (nooit via een portal-schrijfpad)? */
export function isAlleenHrVerlofMethode(
  methode: string | null | undefined,
): methode is AlleenHrVerlofMethode {
  return methode != null && (ALLEEN_HR_VERLOFMETHODEN as readonly string[]).includes(methode)
}

/**
 * De keuze-opties voor een portal-keuzelijst: de centrale set met de GEDEELDE
 * labels erbij. Elke portal-UI die een methode aanbiedt gebruikt deze functie -
 * nooit een eigen labelmap of een eigen opsomming.
 */
export function portalKiesbareVerlofMethodeOpties(): Array<{
  value: PortalKiesbareVerlofMethode
  label: string
}> {
  return PORTAL_KIESBARE_VERLOFMETHODEN.map((value) => ({
    value,
    label: VERLOF_METHODE_LABELS[value],
  }))
}

// ─── Spec 96 - de emmer-kant van dezelfde regel ──────────────────────────────
//
// Een methode die de medewerker niet mag kiezen, mag zijn potje ook niet stil
// meebetalen aan een portal-zelfaanvraag. Ouderschap (kind-emmer) en senioren
// (peildatum-emmer) zijn eigen potjes met een eigen aanvraagpad; alleen de
// KLASSIEKE jaarrecht-emmers financieren een keuze-loze portal-aanvraag of een
// vakantiecampagne. Dit spiegelt de canonieke filter in `leave-v2.ts`
// (`childId: null, peildatum: null, status: DEFINITIEF`).

/** Alle `LeaveEntitlementType`-waarden (Prisma-enum). */
export const VERLOF_EMMER_TYPES = [
  'WETTELIJK',
  'BOVENWETTELIJK',
  'ATV',
  'TVT_WERK',
  'TVT_REIS',
  'OUDERSCHAP_BETAALD',
  'OUDERSCHAP_ONBETAALD',
  'SENIORENVERLOF',
] as const
export type VerlofEmmerType = (typeof VERLOF_EMMER_TYPES)[number]

/** De klassieke jaarrecht-emmers: het enige potje van een portal-zelfaanvraag. */
export const PORTAL_ZELF_BOEKBARE_EMMER_TYPES = [
  'WETTELIJK',
  'BOVENWETTELIJK',
  'ATV',
  'TVT_WERK',
  'TVT_REIS',
] as const
export type PortalZelfBoekbaarEmmerType = (typeof PORTAL_ZELF_BOEKBARE_EMMER_TYPES)[number]

/** Alle emmer-types -> NL-label. Eén bron voor portal, backoffice en campagne. */
export const VERLOF_EMMER_TYPE_LABELS: Record<VerlofEmmerType, string> = {
  WETTELIJK: 'Wettelijk verlof',
  BOVENWETTELIJK: 'Bovenwettelijk verlof',
  ATV: 'ATV',
  TVT_WERK: 'TVT werk',
  TVT_REIS: 'TVT reis',
  OUDERSCHAP_BETAALD: 'Ouderschapsverlof (betaald)',
  OUDERSCHAP_ONBETAALD: 'Ouderschapsverlof (onbetaald)',
  SENIORENVERLOF: 'Seniorenverlof',
}

/** Mag deze emmer een portal-zelfaanvraag/campagne-prognose financieren? */
export function isPortalZelfBoekbaarEmmerType(
  type: string | null | undefined,
): type is PortalZelfBoekbaarEmmerType {
  return type != null && (PORTAL_ZELF_BOEKBARE_EMMER_TYPES as readonly string[]).includes(type)
}

/** Alle `BijzonderReden`-waarden -> NL-label (CAO art. 38). */
export const BIJZONDER_REDEN_LABELS: Record<BijzonderReden, string> = {
  HUWELIJK_EIGEN: 'Eigen huwelijk',
  HUWELIJK_KIND: 'Huwelijk kind',
  HUWELIJK_FAMILIELID: 'Huwelijk familielid',
  OVERLIJDEN_PARTNER_KIND: 'Overlijden partner/kind',
  OVERLIJDEN_OUDER: 'Overlijden ouder',
  OVERLIJDEN_SCHOONOUDER: 'Overlijden schoonouder',
  OVERLIJDEN_FAMILIELID: 'Overlijden familielid',
  VERHUIZING: 'Verhuizing',
  JUBILEUM_25: '25-jarig jubileum',
  DOKTERSBEZOEK: 'Doktersbezoek',
  OVERIG: 'Overig (CAO art. 38)',
}

/**
 * Alle V2-`LeaveStatus`-waarden -> NL-label. Gedeeld met de verlofmodule en het
 * dienstenplein zodat de status overal met dezelfde tekst verschijnt. De drie
 * V1-legacy-statussen (AANGEVRAAGD/GOEDGEKEURD/AFGEWEZEN) horen hier bewust niet:
 * de verlof-exports houden daarvoor hun eigen `LEAVE_STATUS_LABELS`.
 */
export const VERLOF_STATUS_LABELS: Record<VerlofStatus, string> = {
  CONCEPT: 'Concept',
  INGEDIEND: 'Ingediend',
  AANVULLENDE_VRAAG: 'Wacht op antwoord medewerker',
  GOEDGEKEURD_TEAMLEIDER: 'Goedgekeurd door TL',
  AFGEKEURD_TEAMLEIDER: 'Afgewezen door TL',
  GECLASSIFICEERD: 'Geclassificeerd',
  DEFINITIEF: 'Definitief',
  OVERRULED: 'Overruled HR',
  AFGEKEURD: 'Definitief afgewezen',
  INGETROKKEN: 'Ingetrokken',
}

/**
 * Maak van een rauwe enum-string een nette, leesbare NL-fallback wanneer er
 * (nog) geen expliciet label is: `OVERLIJDEN_OUDER` -> "Overlijden ouder".
 * Garandeert dat de UI nooit een kale SCREAMING_SNAKE_CASE-code toont.
 */
function humanizeEnumValue(value: string): string {
  const cleaned = value.trim().toLowerCase().replace(/_+/g, ' ').trim()
  if (cleaned === '') return 'Onbekend'
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

/** Label uit een map, met de humanize-fallback voor onbekende waarden. */
function labelFrom(map: Record<string, string>, value: string): string {
  return map[value] ?? humanizeEnumValue(value)
}

/**
 * Alle `SaldoMutatieBron`-waarden -> NL-label. `Record<string, string>` zodat callers
 * met een rauwe Prisma-enum-string kunnen indexeren zonder cast; onbekende waarden
 * vangt `saldoMutatieBronLabel` af met de humanize-fallback. Eén bron voor de
 * saldo-mutatie-historie (backoffice) zodat die nooit een kale enum-code toont.
 */
export const SALDO_MUTATIE_BRON_LABELS: Record<string, string> = {
  JAARLIJKSE_TOEKENNING: 'Jaarlijkse toekenning',
  CONTRACTWIJZIGING: 'Contractwijziging',
  OVERDRACHT_VOLGEND_JAAR: 'Overdracht volgend jaar',
  HANDMATIGE_CORRECTIE: 'Handmatige correctie',
  WERKBON_OVERUREN: 'Werkbon - overuren',
  WERKBON_REISTIJD: 'Werkbon - reistijd',
  HANDMATIG_TVT: 'Handmatig TVT',
  VERLOFAANVRAAG: 'Verlofaanvraag',
  VERVAL_AUTOMATISCH: 'Verval (automatisch)',
  UITBETALING: 'Uitbetaling',
  KEUZEBUDGET_INRUIL: 'Keuzebudget - inruil',
  MIN_UREN: 'Min-uren',
  OUDERSCHAP_RECHT: 'Ouderschapsrecht',
  SENIOREN_PEILDATUM: 'Seniorenverlof (peildatum)',
  FEESTDAG_COMPENSATIE: 'Feestdagcompensatie',
  // batch-67 hr-brieven (FO-506) — non-actief zonder behoud van verlof.
  NON_ACTIEF_AFBOEKING: 'Non-actiefstelling (afboeking)',
  // batch-61 offboarding-afronding (FO-452) - correctie bij afsluiting uitdienst.
  CORRECTIE_UITDIENST: 'Correctie uit dienst',
  // batch-69 tvt-saldi (FO-522 §C8) - twee gekoppelde mutaties van één verplaatsing.
  SALDO_VERPLAATSING: 'Saldo verplaatst',
  // batch-69 tvt-saldi (FO-513 §C8) - TVT-opbouw bijgesteld na dalende overuren.
  TVT_CLAWBACK: 'TVT-opbouw bijgesteld (overuren gedaald)',
}

/** Alle `SaldoMutatieRichting`-waarden -> NL-label. */
export const SALDO_MUTATIE_RICHTING_LABELS: Record<string, string> = {
  OPBOUW: 'Opbouw',
  AFNAME: 'Afname',
  VERVAL: 'Verval',
  UITBETALING: 'Uitbetaling',
  OVERDRACHT: 'Overdracht',
  CORRECTIE: 'Correctie',
}

/** Bron van een saldo-mutatie -> NL-label (nooit een kale enum-code). */
export function saldoMutatieBronLabel(bron: string): string {
  return labelFrom(SALDO_MUTATIE_BRON_LABELS, bron)
}

/** Richting van een saldo-mutatie -> NL-label (nooit een kale enum-code). */
export function saldoMutatieRichtingLabel(richting: string): string {
  return labelFrom(SALDO_MUTATIE_RICHTING_LABELS, richting)
}

/**
 * Classificeer een verlofverzoek tot één NL-label, exact zoals de verlofmodule:
 * methode-eerst (incl. OUDERSCHAP/SENIOREN), met `bijzonderReden` als verfijning
 * bij methode `BIJZONDER` ("Bijzonder - Overlijden ouder"); valt zonder methode
 * terug op het `type`-label; en nooit op de kale enum (nette fallback).
 *
 * Gedeeld door de verlofmodule en de verlof-exports zodat beide identiek
 * classificeren (FO-071).
 */
export function classificeerVerlof(req: {
  type: string
  methode?: string | null
  bijzonderReden?: string | null
}): string {
  const methode = req.methode?.trim()
  if (methode) {
    const methodeLabel = labelFrom(VERLOF_METHODE_LABELS, methode)
    if (methode === 'BIJZONDER') {
      const reden = req.bijzonderReden?.trim()
      if (reden) return `${methodeLabel} - ${labelFrom(BIJZONDER_REDEN_LABELS, reden)}`
    }
    return methodeLabel
  }
  const type = req.type.trim()
  if (type === '') return 'Onbekend'
  return labelFrom(LEAVE_TYPE_LABELS, type)
}
